/**
 * THE LIBRARY RAIL — the team's saved questions, beside the chat.
 *
 * The measured value of a query assistant landed in PERSISTED OBJECTS, not in
 * conversation: teams using one were 2–3× more likely to build complex queries
 * AND SAVE THEM TO BOARDS. So the library is on screen next to the composer
 * rather than behind a tab nobody opens.
 *
 * `visibility: team` rows are the "for the team" half of that finding — without
 * the team branch, the column would be one nothing reads. A teammate's PRIVATE
 * row is not listed at all (the backend's predicate decides that, and a
 * non-owner private row 404s rather than 403s, so its existence never leaks).
 */
import { Bookmark, Trash2, Users } from 'lucide-react';

import type { ArtifactsController } from '@/hooks/useAgentArtifacts';

export function LibraryRail({
  artifacts,
  onReopen,
}: {
  artifacts: ArtifactsController;
  /** Opens the saved question: its stored invocation is RE-RUN (SavedRunPanel),
   *  never re-asked to the model as free text (spec §16.4 primitive 1). */
  onReopen: (question: { id: string; label: string }) => void;
}) {
  return (
    <aside className="rounded-lg border bg-card p-4" aria-label="Saved questions">
      <div className="flex items-center gap-2">
        <Bookmark className="h-4 w-4 text-muted-foreground" aria-hidden />
        <h3 className="text-sm font-semibold">Saved questions</h3>
      </div>

      {artifacts.savedLoading ? (
        <ul className="mt-3 space-y-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <li key={index} className="h-10 motion-safe:animate-pulse rounded bg-muted" />
          ))}
        </ul>
      ) : artifacts.saved.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Nothing saved yet. A saved question stores the metric and its parameters — never a
          number — so re-opening it always re-runs against today’s data.
        </p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {artifacts.saved.map((question) => (
            <li key={question.id} className="rounded-md border p-2">
              <div className="flex items-start justify-between gap-2">
                <button
                  type="button"
                  onClick={() => onReopen({ id: question.id, label: question.label })}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="block truncate text-xs font-medium">{question.label}</span>
                  <span className="mt-0.5 block truncate font-mono text-[10px] text-muted-foreground">
                    {question.metric_id}
                  </span>
                </button>
                {question.is_owner && (
                  <button
                    type="button"
                    onClick={() => void artifacts.deleteQuestion(question.id)}
                    aria-label={`Delete saved question ${question.label}`}
                    className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-red-600 dark:hover:text-red-400"
                  >
                    <Trash2 className="h-3 w-3" aria-hidden />
                  </button>
                )}
              </div>
              {question.visibility === 'team' && (
                <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  <Users className="h-2.5 w-2.5" aria-hidden />
                  team
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
